import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../App.js';
import { Music, Eye, EyeOff, ArrowRight, Sparkles, KeyRound, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Login() {
  const { login, register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter token & password
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Wait for auth bootstrap (token-in-localStorage → /api/auth/me round-trip) to finish
  // before showing the login form. Otherwise users with an existing valid session briefly
  // see the login page, type credentials into it, and race against the in-flight auth check.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" data-testid="auth-loading-spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, company);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await response.json();
      
      if (response.ok) {
        setResetToken(data.token || '');
        setResetStep(2);
        setResetSuccess('Reset token generated! Enter it below with your new password.');
      } else {
        setResetError(data.detail || 'Failed to request password reset');
      }
    } catch (err) {
      setResetError('Network error. Please try again.');
    }
    setResetLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });
      const data = await response.json();
      
      if (response.ok) {
        setResetSuccess('Password reset successful! You can now login.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetStep(1);
          setResetEmail('');
          setResetToken('');
          setNewPassword('');
          setResetSuccess('');
        }, 2000);
      } else {
        setResetError(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setResetError('Network error. Please try again.');
    }
    setResetLoading(false);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetEmail('');
    setResetToken('');
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1762716514229-739f6769e282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGF1ZGlvJTIwd2F2ZWZvcm0lMjBvcmFuZ2UlMjBtaW5pbWFsfGVufDB8fHx8MTc3MDgxMTUwNXww&ixlib=rb-4.1.0&q=85"
          alt="Audio waveform"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_syncpulse-preview/artifacts/m12tbw8s_2.png" 
              alt="SyncPulse" 
              className="h-40 w-auto object-contain drop-shadow-2xl"
            />
          </div>
          <h1 className="font-heading text-4xl font-extrabold mb-4 leading-tight">
            AI-Powered Music<br />Metadata Generation
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Transform your audio into sync-ready descriptions with intelligent tagging, 
            lyric transcription, and vivid narrative descriptions.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12 bg-[#1a1a2e]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_syncpulse-preview/artifacts/m12tbw8s_2.png" 
              alt="SyncPulse" 
              className="h-10 w-auto object-contain"
            />
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold text-white mb-2">
              {isLogin ? 'Welcome back' : 'Create account'}
            </h2>
            <p className="text-white/60">
              {isLogin ? 'Sign in to access your dashboard' : 'Start your 5-credit free trial'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-xl"
                    placeholder="John Doe"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Company (Optional)</label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-xl"
                    placeholder="Epidemic Sound"
                    data-testid="company-input"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-white mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 input-dark rounded-xl"
                placeholder="you@company.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-white">Password</label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm hover:underline"
                    style={{color: '#F96F51'}}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 input-dark rounded-xl pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-white/50 hover:text-white transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-semibold" style={{color: '#F96F51'}}>
                {isLogin ? 'Sign up' : 'Sign in'}
              </span>
            </button>
          </div>

          {!isLogin && (
            <div className="mt-8 p-4 rounded-xl border border-white/10" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.1), rgba(249,111,81,0.02))'}}>
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 flex-shrink-0" style={{color: '#F96F51'}} />
                <p className="text-sm text-white/60">
                  <span className="font-semibold text-white">5 free credits</span> included to get started. 
                  Each track costs 1 credit to process.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#1a1a2e] rounded-2xl p-8 w-full max-w-md border border-white/10 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.3), rgba(249,111,81,0.1))'}}>
                <KeyRound className="w-6 h-6" style={{color: '#F96F51'}} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-white">Reset Password</h3>
                <p className="text-sm text-white/50">
                  {resetStep === 1 ? 'Enter your email to get a reset token' : 'Enter the token and your new password'}
                </p>
              </div>
            </div>

            {resetStep === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Email Address</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-xl"
                    placeholder="you@company.com"
                    required
                    data-testid="reset-email-input"
                  />
                </div>

                {resetError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    {resetError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="flex-1 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1 py-3 btn-primary flex items-center justify-center gap-2"
                    data-testid="request-reset-btn"
                  >
                    {resetLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Get Reset Token'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {resetSuccess && (
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    {resetSuccess}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-white mb-2">Reset Token</label>
                  <input
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-xl font-mono text-sm"
                    placeholder="Paste your reset token here"
                    required
                    data-testid="reset-token-input"
                  />
                  <p className="text-xs text-white/40 mt-1">The token was shown after requesting the reset</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 input-dark rounded-xl"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>

                {resetError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    {resetError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setResetStep(1)}
                    className="flex-1 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1 py-3 btn-primary flex items-center justify-center gap-2"
                    data-testid="confirm-reset-btn"
                  >
                    {resetLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
